/**
 * VoiceSettings — configure sender voice profile and writing style.
 *
 * Fetches current voice config on mount and saves via PUT /api/settings/voice.
 * All state is immutable; handlers derive new objects from previous state.
 * Gated to Pro plan via PlanGate.
 */

import { useState, useCallback, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, CheckCircle2 } from 'lucide-react'
import type { SenderVoiceConfig } from '@dealcloser/shared'
import { DEFAULT_SENDER_VOICE } from '@dealcloser/shared'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ChipInput } from '@/components/chip-input'
import { PlanGate } from '@/components/plan-gate'
import { useApi } from '@/hooks/use-api'
import { usePlan } from '@/hooks/use-plan'

const voiceKeys = { config: ['settings', 'voice'] as const }

const textareaClass =
  'flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none'

export default function VoiceSettings() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  const plan = usePlan()
  const [form, setForm] = useState<SenderVoiceConfig>(DEFAULT_SENDER_VOICE)
  const [savedSuccess, setSavedSuccess] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: voiceKeys.config,
    queryFn: async (): Promise<SenderVoiceConfig> => {
      const res = await apiFetch<SenderVoiceConfig>('/api/settings/voice')
      if (!res.success || !res.data) return DEFAULT_SENDER_VOICE
      return res.data
    },
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const saveMutation = useMutation({
    mutationFn: async (config: SenderVoiceConfig): Promise<void> => {
      const res = await apiFetch('/api/settings/voice', { method: 'PUT', body: config })
      if (!res.success) throw new Error(res.error ?? 'Failed to save')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: voiceKeys.config })
      setSavedSuccess(true)
      setTimeout(() => setSavedSuccess(false), 3000)
    },
  })

  const setPovAreas = useCallback((povAreas: readonly string[]) => {
    setForm((prev) => ({ ...prev, povAreas }))
  }, [])

  const setCredibilityBullets = useCallback((credibilityBullets: readonly string[]) => {
    setForm((prev) => ({ ...prev, credibilityBullets }))
  }, [])

  const setDoNotSay = useCallback((doNotSay: readonly string[]) => {
    setForm((prev) => ({ ...prev, doNotSay }))
  }, [])

  const isSaving = saveMutation.isPending
  const saveError = saveMutation.error instanceof Error ? saveMutation.error.message : null

  return (
    <PlanGate requiredPlan="pro" currentPlan={plan}>
      <div className="space-y-6">
        {/* Sender Profile */}
        <Card>
          <CardHeader>
            <CardTitle>Sender Profile</CardTitle>
            <CardDescription>Help AI understand who you are so it can write in your voice.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="voice-profile-summary">Profile Summary</Label>
              <textarea
                id="voice-profile-summary"
                rows={4}
                placeholder="Brief summary of who you are and what you do (used by AI to write in your voice)..."
                value={form.profileSummary}
                onChange={(e) => setForm((prev) => ({ ...prev, profileSummary: e.target.value }))}
                disabled={isLoading || isSaving}
                className={textareaClass}
              />
            </div>
          </CardContent>
        </Card>

        {/* Writing Style */}
        <Card>
          <CardHeader>
            <CardTitle>Writing Style</CardTitle>
            <CardDescription>Provide examples and preferences so AI matches your tone.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="voice-sample-1">Writing Sample 1</Label>
              <textarea
                id="voice-sample-1"
                rows={4}
                placeholder="Paste a message or post you wrote that represents your voice..."
                value={form.writingSample1}
                onChange={(e) => setForm((prev) => ({ ...prev, writingSample1: e.target.value }))}
                disabled={isLoading || isSaving}
                className={textareaClass}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="voice-sample-2">Writing Sample 2</Label>
              <textarea
                id="voice-sample-2"
                rows={4}
                placeholder="Paste a second example..."
                value={form.writingSample2}
                onChange={(e) => setForm((prev) => ({ ...prev, writingSample2: e.target.value }))}
                disabled={isLoading || isSaving}
                className={textareaClass}
              />
            </div>

            <ChipInput
              label="Point of View Areas"
              placeholder="Topics you have strong opinions on..."
              chips={form.povAreas}
              onChange={setPovAreas}
              disabled={isLoading || isSaving}
            />

            <ChipInput
              label="Credibility Bullets"
              placeholder="Key achievements, results, or credentials..."
              chips={form.credibilityBullets}
              onChange={setCredibilityBullets}
              disabled={isLoading || isSaving}
            />

            <ChipInput
              label="Do Not Say"
              placeholder="Phrases or topics to avoid..."
              chips={form.doNotSay}
              onChange={setDoNotSay}
              disabled={isLoading || isSaving}
            />
          </CardContent>
        </Card>

        {/* Save */}
        <div className="flex items-center gap-3">
          <Button onClick={() => saveMutation.mutate(form)} disabled={isSaving || isLoading}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Voice Settings'
            )}
          </Button>
          {savedSuccess && (
            <div className="flex items-center gap-1.5 text-sm text-emerald-300 ">
              <CheckCircle2 className="h-4 w-4" />
              Saved successfully
            </div>
          )}
          {saveError && <p className="text-sm text-destructive">{saveError}</p>}
        </div>
      </div>
    </PlanGate>
  )
}
